/**
 * liveSessionTime — l'heure d'une séance, telle que le live de l'élève la lit aujourd'hui.
 *
 *  - la séance en cours : la plus proche des prochaines occurrences non finalisées
 *    (`findClosestNextOccurrence`, `student/account/onboarding/LiveRoomCard.js`, recopié à
 *    l'identique : le moteur n'importe pas le legacy) ;
 *  - l'heure d'arrivée que le tuteur a annoncée (`delay_today`, en minutes) : « 18h10 » ;
 *  - la note demandée en partant : seulement au-delà de quinze minutes de séance
 *    (`common/liveMode/leaveLiveSatisfaction.js`).
 */
import moment from "moment"

export type LiveOccurrenceLike = { date?: string | null; finalized_at?: string | null }
export type LiveSessionTimeLike = { next_occurrences?: LiveOccurrenceLike[] | null; delay_today?: number | null }

/** La prochaine occurrence non finalisée, la plus proche. */
export function closestNextOccurrence(session: LiveSessionTimeLike | null | undefined): LiveOccurrenceLike | null {
    return earliest((session?.next_occurrences ?? []).filter((occurrence) => !occurrence?.finalized_at))
}

/**
 * La plus proche des prochaines occurrences, **finalisée comprise** : la lecture du serveur
 * (`LiveSession::getTodayOrNextOccurrence()` sans `excludeFinalized`), celle qui désigne le tuteur du
 * jour. Une séance finalisée reste celle d'aujourd'hui pour dire qui la tient.
 */
export function todayOrNextOccurrence(session: LiveSessionTimeLike | null | undefined): LiveOccurrenceLike | null {
    return earliest((session?.next_occurrences ?? []).filter((occurrence) => occurrence != null))
}

/** La plus proche d'une liste déjà filtrée (la liste est triée sur place : `filter` l'a copiée). */
function earliest(occurrences: LiveOccurrenceLike[]): LiveOccurrenceLike | null {
    return occurrences.sort((a, b) => moment(a?.date ?? undefined).diff(b?.date ?? undefined))[0] ?? null
}

/** Le début de la séance en cours (`YYYY-MM-DD HH:mm`), ou `null`. */
export function sessionStartOf(session: LiveSessionTimeLike | null | undefined): string | null {
    return closestNextOccurrence(session)?.date ?? null
}

/** L'heure annoncée par le tuteur (« 18h10 »), quand il a prévenu d'un retard. */
export function announcedArrivalOf(session: LiveSessionTimeLike | null | undefined): string | null {
    const delay = session?.delay_today ?? 0
    const start = sessionStartOf(session)
    if (delay <= 0 || start == null) return null
    return moment(start).add(delay, "minutes").format("HH[h]mm")
}

/** La note se demande en partant, une fois la séance commencée depuis quinze minutes. */
export function shouldAskRating(start: string | null, now: moment.Moment = moment()): boolean {
    return start != null && now.isSameOrAfter(moment(start).add(15, "minutes"))
}
